import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import { logout } from '@/shared/store/authSlice';
import { hasAdminRole } from '@/shared/utils/authUtils';
import { can } from '@/shared/auth/useCan';
import type { Capability } from '@/shared/auth/capabilities';
import logo from '@/assets/images/nebi-logo.png';
import styles from './AdminLayout.module.scss';
import clsx from 'clsx';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  capability: Capability;
}

const navItems: NavItem[] = [
  { to: '/admin/analytics', icon: 'insights', label: 'Analytics', capability: 'viewAnalytics' },
  { to: '/admin/forum', icon: 'forum', label: 'Fórum', capability: 'viewForum' },
  {
    to: '/admin/attendance',
    icon: 'fact_check',
    label: 'Lista de presença',
    capability: 'viewAttendance',
  },
  {
    to: '/admin/categories',
    icon: 'category',
    label: 'Categorias',
    capability: 'manageCategories',
  },
  { to: '/admin/courses', icon: 'school', label: 'Cursos', capability: 'viewCourses' },
  {
    to: '/admin/book-clubs',
    icon: 'menu_book',
    label: 'Clube do Livro',
    capability: 'viewBookClubs',
  },
  { to: '/admin/banners', icon: 'view_carousel', label: 'Banners', capability: 'viewBanners' },
  { to: '/admin/coupons', icon: 'sell', label: 'Cupons', capability: 'viewCoupons' },
  { to: '/admin/sales', icon: 'monitoring', label: 'Vendas', capability: 'viewSales' },
  { to: '/admin/professors', icon: 'person', label: 'Professores', capability: 'manageProfessors' },
  { to: '/admin/users', icon: 'groups', label: 'Usuários', capability: 'viewUsers' },
  { to: '/admin/api-clients', icon: 'api', label: 'API', capability: 'manageApiClients' },
];

export const AdminLayout = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = hasAdminRole(token);
  const visibleNavItems = navItems.filter((item) => can(token, item.capability));

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className={styles.layout}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside className={clsx(styles.sidebar, sidebarOpen && styles.sidebarOpen)}>
        <div className={styles.sidebarHeader}>
          <img src={logo} alt="NEBI Admin" className={styles.logo} />
          <span className={styles.adminBadge}>{isAdmin ? 'Admin' : 'Professor'}</span>
        </div>

        <nav className={styles.nav} aria-label="Navegação administrativa">
          <ul className={styles.navList}>
            {visibleNavItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(styles.navLink, isActive && styles.navLinkActive)
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className={`${styles.navIcon} material-icons`} aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <button
            className={styles.logoutButton}
            onClick={handleLogout}
            aria-label="Sair do painel"
          >
            <span className="material-icons" aria-hidden="true">
              logout
            </span>
            Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={styles.main}>
        <header className={styles.topbar}>
          <button
            className={styles.menuToggle}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Abrir menu lateral"
            aria-expanded={sidebarOpen}
          >
            <span className="material-icons">menu</span>
          </button>
          <span className={styles.topbarTitle}>Painel Administrativo</span>
          <a href="/home" className={styles.viewSite} aria-label="Ver site público">
            <span className="material-icons">open_in_new</span>
            Ver site
          </a>
        </header>

        <main className={styles.content} id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
